import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { PoDialog, VendorForm, usePurchasing } from "@/components/PurchasingParts";
import { api, errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";

const TONE = { draft: "bg-amber-100 text-amber-800", approved: "bg-sky-100 text-sky-800", received: "bg-emerald-100 text-emerald-800", cancelled: "bg-gray-100 text-gray-600" };

function PoRow({ po, isAdmin, onDone }) {
  const act = async (a, msg) => { try { await api.post(`/purchase-orders/${po.id}/${a}`); toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid={`po-row-${po.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold">{po.number} · {po.vendor_name}</div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TONE[po.status]}`} data-testid={`po-status-${po.id}`}>{po.status}{po.status === "received" ? (po.paid ? " · paid" : " · unpaid") : ""}</span>
          <span className="font-mono font-semibold">{inr(po.total)}</span>
        </div>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{po.lines.map((l) => `${l.name} ${l.qty} ${l.unit} @ ${inr(l.rate)}`).join(" · ")}</div>
      <div className="mt-1 text-xs text-muted-foreground">Raised {fmtDate(po.date)} by {po.created_by}{po.expected_date ? ` · expected ${fmtDate(po.expected_date)}` : ""}{po.approved_by ? ` · approved by ${po.approved_by}` : ""}</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {isAdmin && po.status === "draft" && <Button size="sm" onClick={() => act("approve", "PO approved")} className="bg-[#0F382C] hover:bg-[#134E3A]" data-testid={`po-approve-${po.id}`}>Approve</Button>}
        {po.status === "approved" && <Button size="sm" onClick={() => act("receive", "Goods received, stock updated")} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`po-receive-${po.id}`}>Mark goods received</Button>}
        {isAdmin && po.status === "received" && !po.paid && <Button size="sm" variant="outline" onClick={() => act("paid", "Marked as paid")} data-testid={`po-paid-${po.id}`}>Mark paid</Button>}
        {isAdmin && ["draft", "approved"].includes(po.status) && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => act("cancel", "PO cancelled")} data-testid={`po-cancel-${po.id}`}>Cancel</Button>}
      </div>
    </div>
  );
}

export default function Purchasing() {
  const { vendors, pos, materials, load, isAdmin } = usePurchasing();
  const [open, setOpen] = useState(false);
  const unpaid = pos.filter((p) => p.status === "received" && !p.paid).reduce((s, p) => s + p.total, 0);
  return (
    <div data-testid="purchasing-page">
      <PageHeader eyebrow="Procurement" title="Suppliers & Purchase Orders" subtitle={`Unpaid to suppliers: ${inr(unpaid)}`}>
        <Button onClick={() => setOpen(true)} disabled={!vendors.length} className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid="new-po-btn"><Plus className="mr-1 h-4 w-4" />New purchase order</Button>
      </PageHeader>
      <section className="mb-10">
        <h2 className="mb-3 font-display text-lg font-semibold text-[#0F382C]">Suppliers ({vendors.length})</h2>
        {isAdmin && <VendorForm onDone={load} />}
        <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {vendors.map((v) => (
            <div key={v.id} className="rounded-xl border border-[#E7E2D6] bg-white p-3 text-sm" data-testid={`vendor-card-${v.id}`}>
              <div className="font-semibold">{v.name}</div>
              <div className="text-xs text-muted-foreground">{[v.phone, v.gstin && `GSTIN ${v.gstin}`, v.materials, v.payment_terms].filter(Boolean).join(" · ")}</div>
            </div>
          ))}
        </div>
      </section>
      <h2 className="mb-3 font-display text-lg font-semibold text-[#0F382C]">Purchase orders</h2>
      <div className="space-y-3">
        {pos.map((po) => <PoRow key={po.id} po={po} isAdmin={isAdmin} onDone={load} />)}
        {!pos.length && <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="po-empty">No purchase orders yet. Add a supplier, then raise a PO.</div>}
      </div>
      <PoDialog open={open} onClose={() => setOpen(false)} vendors={vendors} materials={materials} onDone={load} />
    </div>
  );
}
